import { createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { toast } from "react-toastify";

import { REQUEST_STATUSES } from "@/constants";
import { ExperienceMediaItemService } from "@/services";

import {
  addExperienceMediaItems,
  removeExperienceMediaItems,
  setExperienceMediaItems,
  setExperienceMediaItemsRequestStatus,
} from "./experienceMediaItemsSlice";

// Types
interface FetchExperienceMediaItemsParams {
  propertyId: string | number;
  experienceId: string | number;
  query?: Record<string, string | number | boolean>;
}

interface AddExperienceMediaItemsParams {
  propertyId: string | number;
  experienceId: string | number;
  mediaItems: { id: number }[];
  options?: { showToast?: boolean };
}

interface RemoveExperienceMediaItemsParams {
  propertyId: string | number;
  experienceId: string | number;
  mediaItems: { id: number }[];
}

interface ExperienceMediaItem {
  id: number;
  mediaItemId: number;
  propertyId: number;
  experienceId: number;
  createdAt?: string;
  updatedAt?: string;
  createdById?: number | null;
}

interface ExperienceMediaItemState {
  experienceMediaItems: Record<number, ExperienceMediaItem>;
  status: REQUEST_STATUSES;
  err: string | null;
}

interface ExperienceMediaItemsState {
  [key: string]: ExperienceMediaItemState;
}

interface RootState {
  experienceMediaItems: ExperienceMediaItemsState;
}

// Async thunks for Experience Media Items
export const fetchExperienceMediaItems = createAsyncThunk(
  "experienceMediaItems/fetchExperienceMediaItems",
  async ({ propertyId, experienceId, query = {} }: FetchExperienceMediaItemsParams, { dispatch }) => {
    dispatch(setExperienceMediaItemsRequestStatus({ propertyId, experienceId, status: REQUEST_STATUSES.LOADING }));

    try {
      const { resp, status, err } = await ExperienceMediaItemService.getExperienceMediaItems(
        propertyId,
        experienceId,
        query,
      );

      const experienceMediaItems = resp.data.reduce(
        (acc: Record<number, ExperienceMediaItem>, item: ExperienceMediaItem) => ({ ...acc, [item.mediaItemId]: item }),
        {},
      );

      dispatch(
        setExperienceMediaItems({
          propertyId,
          experienceId,
          experienceMediaItems,
          status,
          err: err && typeof err === "object" && "message" in err ? String(err.message) : null,
        }),
      );

      return { resp, status, err };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      dispatch(
        setExperienceMediaItems({
          propertyId,
          experienceId,
          experienceMediaItems: {},
          status: REQUEST_STATUSES.FAILED,
          err: errorMessage,
        }),
      );
      throw error;
    }
  },
);

export const addExperienceMediaItemsThunk = createAsyncThunk(
  "experienceMediaItems/addExperienceMediaItems",
  async (
    { propertyId, experienceId, mediaItems, options = { showToast: true } }: AddExperienceMediaItemsParams,
    { dispatch, getState },
  ) => {
    const { showToast } = options;

    if (!mediaItems.length) {
      return { success: true };
    }

    try {
      const key = `${propertyId}/${experienceId}`;
      const state = getState() as RootState;
      const currentExperienceMediaItems = cloneDeep(state.experienceMediaItems[key]?.experienceMediaItems) || {};
      let newCurrentExperienceMediaItems: Record<number, ExperienceMediaItem>;

      if (mediaItems.length < 3) {
        const results = await Promise.all(
          mediaItems.map((item) =>
            ExperienceMediaItemService.addExperienceMediaItem(propertyId, experienceId, item.id),
          ),
        );

        newCurrentExperienceMediaItems = results.reduce((acc, item) => {
          const { mediaItemId } = item.resp.data;

          return {
            ...acc,
            [mediaItemId]: item.resp.data,
          };
        }, currentExperienceMediaItems);
      } else {
        const results = await ExperienceMediaItemService.bulkAddExperienceMediaItem(propertyId, experienceId, {
          mediaItemIds: mediaItems.map((mediaItem) => mediaItem.id),
        });
        newCurrentExperienceMediaItems = results.resp.data.reduce(
          (acc: Record<number, ExperienceMediaItem>, mediaItem: ExperienceMediaItem) => {
            const { mediaItemId } = mediaItem;

            return {
              ...acc,
              [mediaItemId]: mediaItem,
            };
          },
          currentExperienceMediaItems,
        );
      }

      dispatch(
        addExperienceMediaItems({
          propertyId,
          experienceId,
          experienceMediaItems: newCurrentExperienceMediaItems,
        }),
      );

      if (showToast) {
        toast.success(`Added ${mediaItems.length} media item${mediaItems.length > 1 ? "s" : ""}`, {
          containerId: "dark",
          autoClose: 1500,
        });
      }

      return { success: true };
    } catch (error) {
      if (showToast) {
        toast.error("Failed to add media items.", { containerId: "dark" });
      }
      throw error;
    }
  },
);

export const removeExperienceMediaItemsThunk = createAsyncThunk(
  "experienceMediaItems/removeExperienceMediaItems",
  async ({ propertyId, experienceId, mediaItems }: RemoveExperienceMediaItemsParams, { dispatch, getState }) => {
    if (!mediaItems.length) {
      return { success: true };
    }

    try {
      const key = `${propertyId}/${experienceId}`;
      const state = getState() as RootState;
      const currentExperienceMediaItems = cloneDeep(state.experienceMediaItems[key]?.experienceMediaItems) || {};
      let newCurrentExperienceMediaItems: Record<number, ExperienceMediaItem>;

      if (mediaItems.length < 3) {
        const results = await Promise.all(
          mediaItems.map((item) =>
            ExperienceMediaItemService.deleteExperienceMediaItem(propertyId, experienceId, item.id),
          ),
        );

        newCurrentExperienceMediaItems = results.reduce((acc, item) => {
          const { mediaItemId } = item.resp.data;
          delete acc[mediaItemId];

          return acc;
        }, currentExperienceMediaItems);
      } else {
        const results = await ExperienceMediaItemService.bulkDeleteExperienceMediaItem(propertyId, experienceId, {
          mediaItemIds: mediaItems.map((mediaItem) => mediaItem.id),
        });

        newCurrentExperienceMediaItems = results.resp.data.reduce(
          (acc: Record<number, ExperienceMediaItem>, mediaItemId: number) => {
            delete acc[mediaItemId];

            return acc;
          },
          currentExperienceMediaItems,
        );
      }

      dispatch(
        removeExperienceMediaItems({
          propertyId,
          experienceId,
          experienceMediaItems: newCurrentExperienceMediaItems,
        }),
      );

      toast.success(`Removed ${mediaItems.length} media item${mediaItems.length > 1 ? "s" : ""}`, {
        containerId: "dark",
        autoClose: 1500,
      });

      return { success: true };
    } catch (error) {
      toast.error(`Failed to remove media items.`, { containerId: "dark" });
      throw error;
    }
  },
);
